import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, CandlestickChart as CandleIcon, Activity } from "lucide-react";
import { api, fmtPrice } from "@/lib/api";
import PriceChart from "@/components/PriceChart";
import CandleChart from "@/components/CandleChart";

const INSTRUMENTS = ["EUR/USD","GBP/USD","USD/JPY","AUD/USD","USD/CAD","USD/CHF","NZD/USD"];
const INTERVALS = [
  { ms: 5000,  label: "5s"  },
  { ms: 10000, label: "10s" },
  { ms: 30000, label: "30s" },
  { ms: 60000, label: "1m"  },
];

export default function Markets() {
  const [rates, setRates] = useState({});
  const [active, setActive] = useState("EUR/USD");
  const [chartType, setChartType] = useState("candle"); // 'line' | 'candle'
  const [interval, setIntervalMs] = useState(10000);
  const [marketOpen, setMarketOpen] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/market/rates");
        const map = {};
        data.rates.forEach((r) => (map[r.symbol] = r));
        setRates(map);
        if (data.rates[0]) setMarketOpen(!!data.rates[0].market_open);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);

  const r = rates[active];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-px bg-border min-h-[600px]" data-testid="markets-page">
      <aside className="bg-card overflow-y-auto">
        <div className="px-4 py-3 border-b border-subtle sticky top-0 bg-card">
          <p className="label-eyebrow">Watchlist</p>
        </div>
        <ul>
          {INSTRUMENTS.map((sym) => {
            const q = rates[sym];
            const up = q ? q.change >= 0 : true;
            return (
              <li key={sym}>
                <button
                  onClick={() => setActive(sym)}
                  data-testid={`watchlist-${sym.replace("/", "-")}`}
                  className={`w-full text-left px-4 py-3 border-b border-subtle transition-colors flex items-center justify-between ${
                    active === sym ? "bg-primary/10 border-l-2 border-l-primary" : "hover:bg-surface-hover border-l-2 border-l-transparent"
                  }`}
                >
                  <div>
                    <div className="text-sm font-semibold">{sym}</div>
                    <div className="font-mono-num text-xs text-muted-foreground mt-0.5">
                      {q ? fmtPrice(sym, q.mid) : "—"}
                    </div>
                  </div>
                  {q && (
                    <div className={`flex items-center gap-1 font-mono-num text-xs ${up ? "text-profit" : "text-loss"}`}>
                      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {up ? "+" : ""}{q.change_pct.toFixed(2)}%
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <div className="bg-card p-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <p className={`label-eyebrow ${marketOpen ? "" : "text-warning"}`}>
                {marketOpen ? "Live chart" : "Frozen chart"}
              </p>
              <span
                data-testid="market-status-badge"
                className={`text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded ${
                  marketOpen ? "bg-profit/15 text-profit" : "bg-warning/15 text-warning"
                }`}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: "currentColor" }} />
                {marketOpen ? "Market Open" : "Market Closed"}
              </span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight mt-1">{active}</h2>
          </div>
          <div className="flex items-center gap-3">
            {chartType === "candle" && (
              <div className="flex gap-px bg-border" data-testid="timeframe-group">
                {INTERVALS.map((it) => (
                  <button
                    key={it.ms}
                    onClick={() => setIntervalMs(it.ms)}
                    data-testid={`tf-${it.label}`}
                    className={`px-3 h-9 text-xs font-mono-num font-semibold transition-colors ${
                      interval === it.ms ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-surface-hover"
                    }`}
                  >
                    {it.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-px bg-border">
              <button
                onClick={() => setChartType("line")}
                data-testid="chart-type-line"
                className={`px-3 h-9 text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  chartType === "line" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-surface-hover"
                }`}
              >
                <Activity className="w-3.5 h-3.5" /> LINE
              </button>
              <button
                onClick={() => setChartType("candle")}
                data-testid="chart-type-candle"
                className={`px-3 h-9 text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  chartType === "candle" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-surface-hover"
                }`}
              >
                <CandleIcon className="w-3.5 h-3.5" /> CANDLES
              </button>
            </div>
          </div>
        </div>

        {chartType === "candle" ? (
          <CandleChart key={`c-${active}-${interval}`} symbol={active} height={400} intervalMs={interval} />
        ) : (
          <PriceChart key={`l-${active}`} symbol={active} height={400} />
        )}

        {r && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
            <Stat label="BID (Sell)" value={fmtPrice(active, r.bid)} cls="text-loss" />
            <Stat label="ASK (Buy)"  value={fmtPrice(active, r.ask)} cls="text-profit" />
            <Stat label="MID"        value={fmtPrice(active, r.mid)} />
            <Stat label="CHANGE"     value={`${r.change >= 0 ? "+" : ""}${r.change_pct.toFixed(3)}%`} cls={r.change >= 0 ? "text-profit" : "text-loss"} />
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, cls = "" }) {
  return (
    <div className="bg-card p-4 border border-subtle">
      <div className="label-eyebrow mb-2">{label}</div>
      <div className={`font-mono-num text-xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
